import {action, computed, makeAutoObservable, observable, runInAction} from 'mobx';
import {UserInfo} from "../model/user/UserInfo";
import {UserRole} from "../model/user/UserRole";
import {userApi} from "../backend/UserApi";
import {logEvent} from "../utils/SentryUtils";
import {ReadCompanyDto} from "../model/company/ReadCompanyDto";
import {companyApi} from "../backend/CompanyApi";

export enum CompaniesStoreStates {
    REFRESHING,
    DONE,
    ERROR
}

const defaultValue = {id: '', currentRole: UserRole.CLIENT, allowedRoles: [UserRole.CLIENT]};

class UserInfoStore {
    @observable email: string = '';
    @observable userInfo: UserInfo = defaultValue

    readonly companies = observable<ReadCompanyDto>([])
    @observable companiesLoadingState: CompaniesStoreStates = CompaniesStoreStates.REFRESHING

    constructor() {
        makeAutoObservable(this);
    }

    @action
    public async refreshUser() {
        if (this.isAuthorized) {
            this.userInfo = await userApi.fetchUserInfo();
            logEvent("user", "Fetched user info");
            this.refreshCompanies();
        } else {
            throw new Error("No auth provided");
        }
    }

    public canSelectRoles = () => {
        return this.userInfo.allowedRoles.length > 1;
    }

    @action
    public setEmail(email: string) {
        this.email = email;
    }

    @computed
    public get isAuthorized(): boolean {
        return this.email !== '';
    }

    @action
    public async refreshCompanies() {
        this.companiesLoadingState = CompaniesStoreStates.REFRESHING;
        if (userInfoStore.isAuthorized) {
            try {
                const companies = await companyApi.fetchCompanies();
                runInAction(() => {
                    this.companies.replace(companies);
                    this.companiesLoadingState = CompaniesStoreStates.DONE
                })

            } catch (e) {
                console.error(e)
                runInAction(() => {
                    this.companiesLoadingState = CompaniesStoreStates.ERROR
                })
            }
        } else {
            this.companies.clear()
        }
    }

    public findById = (id?: string) => {
        if (id) {
            const companies = this.companies;
            const selectedCompany = companies.filter(c => c.id === id);
            if (selectedCompany.length === 1) {
                return selectedCompany[0]
            }
        }
        return null;
    }
}

export const userInfoStore = new UserInfoStore();